import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { ROLE_DESCRIPTIONS, ROLE_LABELS } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { useI18n } from '@/i18n/useI18n';
import { NAV_MODULES } from '@/layout/nav';
import { useTenantSettings } from '@/tenant/useTenantSettings';
import { Badge } from '@/ui/Badge';
import { Card, CardBody, CardHeader } from '@/ui/Card';
import { Table, Td, Th } from '@/ui/Table';

/**
 * Vista general.
 *
 * En este issue (#37) es el **estado del andamio**: con qué sesión se entró,
 * en qué locale se está formateando y qué módulos existen. Los contadores
 * reales —escaneos, tasa de autenticación, alertas abiertas— necesitan
 * endpoints de agregados que todavía no están en el OpenAPI; inventarlos
 * ahora sería mostrar números falsos en una pantalla de seguridad, que es
 * justo lo que hace que un equipo deje de confiar en el sistema.
 */
export function OverviewPage(): ReactNode {
  const { t, fmt } = useI18n();
  const { claims } = useAuth();
  const tenant = useTenantSettings();

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <Card>
        <CardHeader
          title={t('module.overview')}
          description={t('module.overview.description')}
        />
        <CardBody>
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2">
            <Row label={t('auth.login.tenant')} value={`${tenant.name} (${tenant.slug})`} />
            <Row label={t('auth.login.email')} value={claims?.email ?? '—'} />
            <Row
              label={t('auth.role.label')}
              value={
                claims === null ? (
                  '—'
                ) : (
                  <span className="flex items-center gap-2">
                    <Badge tone="neutral">{ROLE_LABELS[claims.role]}</Badge>
                    <span className="text-xs text-ink-subtle">
                      {ROLE_DESCRIPTIONS[claims.role]}
                    </span>
                  </span>
                )
              }
            />
            <Row
              label="Locale · huso"
              value={`${fmt.locale} · ${fmt.timeZone}`}
            />
            <Row
              label="Sesión válida hasta"
              value={
                claims === null
                  ? '—'
                  : `${fmt.dateTime(claims.expiresAt)} (${fmt.relative(claims.expiresAt)})`
              }
            />
          </dl>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={t('nav.title')} description={t('placeholder.title')} />
        <Table caption={t('nav.title')}>
          <thead>
            <tr>
              <Th>{t('nav.title')}</Th>
              <Th>{t('auth.role.label')}</Th>
              <Th>{t('placeholder.issue')}</Th>
            </tr>
          </thead>
          <tbody>
            {NAV_MODULES.map((module) => (
              <tr key={module.id}>
                <Td>
                  <div className="flex items-center gap-2">
                    {module.status === 'ready' ? (
                      <Link
                        to={module.path}
                        className="font-medium text-accent underline underline-offset-2"
                      >
                        {t(module.labelKey)}
                      </Link>
                    ) : (
                      <span className="text-ink-muted">{t(module.labelKey)}</span>
                    )}
                    {module.status === 'pending' && (
                      <Badge tone="neutral">{t('nav.pending')}</Badge>
                    )}
                  </div>
                  <p className="text-xs text-ink-subtle">{t(module.descriptionKey)}</p>
                </Td>
                <Td className="text-ink-muted">{ROLE_LABELS[module.minRole]}</Td>
                <Td className="text-ink-muted">
                  {module.issue === null ? '—' : `#${module.issue}`}
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      </Card>
    </div>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): ReactNode {
  return (
    <div>
      <dt className="text-xs tracking-wide text-ink-subtle uppercase">{label}</dt>
      <dd className="mt-0.5 text-sm text-ink">{value}</dd>
    </div>
  );
}
